/**
 * Select dropdown utilities.
 * Provides click-outside handling for multi-select dropdowns.
 */

'use strict';

import type { DotNetObjectReference } from '../types.js';

/**
 * Map to store click handlers for each dropdown instance.
 */
const clickHandlers = new Map<HTMLElement, (event: MouseEvent) => void>();

/**
 * Initialize click-outside detection for a select dropdown.
 * Calls the CloseDropdown method on the .NET component when clicking outside.
 * 
 * @param dotNetRef - Reference to the Blazor component
 * @param element - The dropdown wrapper element
 */
export function initializeClickOutside(
    dotNetRef: DotNetObjectReference,
    element: HTMLElement | null
): void {
    if (!element || !dotNetRef) {
        return;
    }

    // Remove existing handler if present
    disposeClickOutside(element);

    // Create new handler
    const handler = (event: MouseEvent): void => {
        const target = event.target as Node;
        if (!element.contains(target)) {
            dotNetRef.invokeMethodAsync('CloseDropdown').catch(() => {
                // Silently handle disposal/navigation cases
            });
        }
    };

    // Store handler for cleanup
    clickHandlers.set(element, handler);

    // Add listener
    document.addEventListener('click', handler);
}

/**
 * Remove click-outside detection for a select dropdown.
 * 
 * @param element - The dropdown wrapper element
 */
export function disposeClickOutside(element: HTMLElement | null): void {
    if (!element) {
        return;
    }

    const handler = clickHandlers.get(element);
    if (handler) {
        document.removeEventListener('click', handler);
        clickHandlers.delete(element);
    }
}

/**
 * Dispose all click-outside handlers.
 * Called during cleanup or navigation.
 */
export function disposeAll(): void {
    clickHandlers.forEach((handler) => {
        document.removeEventListener('click', handler);
    });
    clickHandlers.clear();
}
